import React from 'react';
import { Button } from '@/components/ui';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import type { TaskOption } from '../types';
import { formatTaskHead, formatTaskLabel, isStandingTask } from '../utils/poPrefill';
import { filterTasksByQuery, mergeTaskOptions } from '../utils/poTaskFilter';

export interface HiredEquipmentTaskPickerProps {
  selected: TaskOption | null;
  onChange: (task: TaskOption | null) => void;
  searchTasks: (query: string) => Promise<TaskOption[]>;
  /**
   * When a PO is already selected, an empty/short query lists these tasks.
   * Typing 2+ characters searches all tasks (not only the PO).
   * `undefined` = global type-ahead only.
   */
  scopedOptions?: TaskOption[];
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  /**
   * Adds a "Create new task..." action to the bottom of the menu. Omit to hide it
   * (no create permission, or surfaces that should not create tasks).
   */
  onCreateTask?: () => void;
  /** Adds a button beside the picker that opens the selected task's details. */
  onViewTask?: (task: TaskOption) => void;
}

function renderTaskOption(task: TaskOption): React.ReactNode {
  const project = task.projectName?.trim();
  return (
    <span className="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-0.5 tw-text-left">
      <span className="tw-flex tw-items-center tw-gap-1.5">
        <span>{formatTaskHead(task)}</span>
        {/* C2: picking a Standing task creates a new Specific task from it. */}
        {isStandingTask(task) ? (
          <span
            className="tw-rounded tw-border tw-border-line tw-bg-surface-alt tw-px-1.5 tw-text-xs tw-font-semibold tw-text-content-secondary"
            title="Standing task – picking it creates a new Specific task"
          >
            Standing
          </span>
        ) : null}
      </span>
      {project ? <span className="tw-text-xs tw-opacity-80">{project}</span> : null}
    </span>
  );
}

function searchText(task: TaskOption): string {
  return [task.taskNumber, task.description, task.name, task.projectName].filter(Boolean).join(' ');
}

const GLOBAL_SEARCH_MIN_CHARS = 2;

const HiredEquipmentTaskPicker: React.FC<HiredEquipmentTaskPickerProps> = ({
  selected,
  onChange,
  searchTasks,
  scopedOptions,
  invalid = false,
  disabled = false,
  placeholder = 'Type a task number or description...',
  allowClear = true,
  onCreateTask,
  onViewTask,
}) => {
  const [fetchedTasks, setFetchedTasks] = React.useState<TaskOption[]>([]);
  const scoped = scopedOptions !== undefined;

  const fetchAndCache = React.useCallback(
    async (query: string) => {
      const results = await searchTasks(query);
      setFetchedTasks(results);
      return results;
    },
    [searchTasks],
  );

  const fetchOptions = React.useCallback(
    async (query: string) => {
      const q = query.trim();
      if (scoped) {
        const poMatches = filterTasksByQuery(scopedOptions, q);
        if (q.length < GLOBAL_SEARCH_MIN_CHARS) return poMatches;
        const remote = await fetchAndCache(q);
        return mergeTaskOptions(poMatches, remote);
      }
      return fetchAndCache(q);
    },
    [scoped, scopedOptions, fetchAndCache],
  );

  const resolve = (id: number): TaskOption =>
    scopedOptions?.find((task) => task.id === id)
    ?? fetchedTasks.find((task) => task.id === id)
    ?? (selected?.id === id ? selected : { id, description: `Task ${id}` });

  const localOptions = selected ? [selected] : [];

  const dropdown = (
    <SearchableDropdown<TaskOption>
      options={localOptions}
      value={selected?.id}
      onChange={(value) => {
        if (!value) {
          onChange(null);
          return;
        }
        onChange(resolve(Number(value)));
      }}
      formatOption={formatTaskLabel}
      formatDisplay={formatTaskHead}
      renderOption={renderTaskOption}
      getSearchText={searchText}
      getValue={(task) => task.id}
      placeholder={placeholder}
      disabled={disabled}
      fetchOptions={fetchOptions}
      minCharsForSearch={scoped ? 0 : GLOBAL_SEARCH_MIN_CHARS}
      noOptionsMessage={
        scoped
          ? (scopedOptions.length === 0
            ? 'No tasks on this purchase order'
            : 'No matching tasks')
          : 'Type at least 2 characters to search by number or description'
      }
      noResultsMessage="No matching tasks"
      allowClear={allowClear}
      preserveOptionsOrder={scoped}
      menuMinWidth={576}
      className={invalid ? 'tw-border-2 tw-border-danger tw-bg-danger/20' : undefined}
      footerAction={
        onCreateTask
          ? { label: 'Create new task...', icon: 'bi-plus-lg', onClick: onCreateTask }
          : undefined
      }
    />
  );

  if (!onViewTask) return dropdown;

  return (
    <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
      <div className="tw-min-w-0 tw-flex-1">{dropdown}</div>
      {selected && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="tw-shrink-0 tw-px-1.5"
          title="View task details"
          aria-label={`View task ${formatTaskHead(selected)}`}
          disabled={disabled}
          onClick={() => onViewTask(selected)}
        >
          <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
};

export default HiredEquipmentTaskPicker;

